//Boilerplate
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

//OurStuff
import { getRoleRights, addRoleRights, deleteRoleRights } from "actions/user/rolerights";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import MGrid, { ACTION_DOWNLOAD } from "components/Shared/MGrid.tsx";
import { MSpinner } from "components/Shared";
import { Capitalize } from "components/Shared/Formatters";


class RoleRights extends React.Component {
  constructor(props) {
    super(props);
    this.getColumnDefinitions = this.getColumnDefinitions.bind(this);
    this.getGridData = this.getGridData.bind(this);
    this.setSelectedSourceRowIndex = this.setSelectedSourceRowIndex.bind(this);
    this.sourceResults = this.sourceResults.bind(this);
    this.limitTargetResults = this.limitTargetResults.bind(this);
    this.switchColumns = this.switchColumns.bind(this);
    this.getGridActions = this.getGridActions.bind(this);
    this.getActionClass = this.getActionClass.bind(this);
    this.getActionHelpText = this.getActionHelpText.bind(this);
    this.getGridHeaderText = this.getGridHeaderText.bind(this);
    this.updateSelectedTargetColumns = this.updateSelectedTargetColumns.bind(this);
    this.getSourceId = this.getSourceId.bind(this);
    this.getTargetIds = this.getTargetIds.bind(this);
    this.getTargetNames = this.getTargetNames.bind(this);
    this.submitChanges = this.submitChanges.bind(this);
    this.getConfirmationModalText = this.getConfirmationModalText.bind(this);
    this.limitByActionAndViewing = this.limitByActionAndViewing.bind(this);
    this.getTargetGridColumnHeaderText = this.getTargetGridColumnHeaderText.bind(this);
    this.state = {
      columnOrder: ["roles", "permissions"],
      selectedSourceRowIndex: null,
      currentAction: null,
      selectedTargetRowIndices: [],
      confirmationModalVisible: false,
      viewing: "enabled",
    };
  }

  componentDidMount() {
    const {
      roleRightsViewModel,
      fetchingRoleRights,
      getRoleRights
    } = this.props;
    if (!roleRightsViewModel && !fetchingRoleRights) {
      getRoleRights();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedSourceRowIndex } = this.state;
    const { requestedRoleRightsEdit, getRoleRights, fetchingRoleRights } = this.props;
    if (prevState.selectedSourceRowIndex && !selectedSourceRowIndex) {
      this.setState({
        currentAction: null,
        selectedTargetRowIndices: [],
        viewing: "enabled"
      });
    }
    if (!requestedRoleRightsEdit && prevProps.requestedRoleRightsEdit) {
      this.setState({
        confirmationModalVisible: false,
      });
      getRoleRights();
    }
    if (!fetchingRoleRights && prevProps.fetchingRoleRights) {
      this.setState({
        currentAction: null,
        selectedTargetRowIndices: [],
      });
    }
  }

  getColumnDefinitions(column) {
    const { columnOrder, viewing, selectedSourceRowIndex } = this.state;
  
    // right grid = columnOrder[1]; left grid = columnOrder[0]
    const isTarget = column === columnOrder[1];
    const hasSelection = selectedSourceRowIndex !== null;
  
    // Only the target grid reflects enabled/disabled; source grid is plain
    const firstColHeader =
      isTarget && hasSelection
        ? `${Capitalize(viewing)} ${Capitalize(column)}`   // "Enabled Permissions" / "Disabled Roles"
        : Capitalize(column);                              // "Roles" or "Permissions"
  
    const cols = [
      {
        key: "name",
        name: firstColHeader,
        dataSource: "name",
        resizable: true
      }
    ];
  
    if (column === "permissions") {
      cols.push({
        key: "description",
        name: "Description",
        dataSource: "description",
        resizable: true,
        tooltip: "description"
      });
    }
  
    return cols;
  }

  getGridData(column) {
    const { columnOrder } = this.state;
    if (column === columnOrder[0]) {
      return this.sourceResults();
    } else {
      return this.limitTargetResults();
    }
  }

  setSelectedSourceRowIndex(index) {
    const { selectedSourceRowIndex } = this.state;
    if (index !== selectedSourceRowIndex) {
      this.setState({
        selectedSourceRowIndex: index,
      });
    } else {
      this.setState({
        selectedSourceRowIndex: null,
        currentAction: null,
        selectedTargetRowIndices: [],
        viewing: "enabled"
      });
    }
  }

  sourceResults() {
    const { roleRightsViewModel } = this.props;
    const { columnOrder } = this.state;
    return roleRightsViewModel[columnOrder[0]];
  }

  limitTargetResults() {
    const { roleRightsViewModel } = this.props;
    const { selectedSourceRowIndex, columnOrder } = this.state;
    if (selectedSourceRowIndex === null) {
      return [];
    }
    let selectedRow = roleRightsViewModel[columnOrder[0]][selectedSourceRowIndex];
    let targetRows = roleRightsViewModel[columnOrder[1]];
    return targetRows.filter(targetRow => {
      return this.limitByActionAndViewing(selectedRow, targetRow);
    });
  }

  limitByActionAndViewing(selectedRow, targetRow) {
    const { viewing, currentAction } = this.state;
    switch (`${currentAction || "removing"}-${viewing || "enabled"}`) {
      case "adding-enabled":
        return !(selectedRow.enabledRightIds || selectedRow.enabledRoleIds).includes(
          targetRow.id
        );
      case "adding-disabled":
        return !(selectedRow.disabledRightIds || selectedRow.disabledRoleIds).includes(
          targetRow.id
        );
      case "removing-enabled":
        return (selectedRow.enabledRightIds || selectedRow.enabledRoleIds).includes(
          targetRow.id
        );
      case "removing-disabled":
        return (selectedRow.disabledRightIds || selectedRow.disabledRoleIds).includes(
          targetRow.id
        );
    }
  }

  switchColumns() {
    const { columnOrder } = this.state;
    this.setState({
      columnOrder: [columnOrder[1], columnOrder[0]],
      selectedSourceRowIndex: null,
    });
  }

  getGridActions() {
    const { selectedSourceRowIndex, currentAction, viewing, columnOrder } = this.state;
    const { user } = this.props;
    const { rights } = user;

    let actions = [];
    if (!currentAction) {
      actions.push(
        {
          icon: "compare_arrows",
          helpText: "Switch Columns",
          order: 9702,
          onClick: this.switchColumns
        }
      );
    }
    if (selectedSourceRowIndex !== null && !currentAction) {
      actions.push(
        {
          icon: viewing === "enabled" ? "desktop_windows" : "desktop_access_disabled",
          helpText: Capitalize(`view ${viewing === "enabled" ? "disabled" : "enabled"} ${columnOrder[1]}`),
          order: 9703,
          onClick: () => {
            if (viewing === "enabled") {
              this.setState({
                viewing: "disabled"
              });
              return;
            }
            this.setState({
              viewing: "enabled"
            });
          }
        },
        { code: ACTION_DOWNLOAD, order: 10000 }
      );
    }
    if (selectedSourceRowIndex !== null && rights.includes("Role Rights Edit") && !currentAction) {
      actions.push(
        {
          icon: "add",
          helpText: this.getActionHelpText("add"),
          order: 9700,
          className: this.getActionClass("adding"),
          onClick: () => {
            if (currentAction === "adding") {
              this.setState({
                currentAction: null,
                selectedTargetRowIndices: [],
              });
            }
            else {
              this.setState({
                currentAction: "adding",
                selectedTargetRowIndices: [],
              });
            }
          }
        },
        {
          icon: "remove",
          helpText: this.getActionHelpText("remove"),
          order: 9701,
          className: this.getActionClass("removing"),
          onClick: () => {
            if (currentAction === "removing") {
              this.setState({
                currentAction: null,
                selectedTargetRowIndices: [],
              });
            }
            else {
              this.setState({
                currentAction: "removing",
                selectedTargetRowIndices: [],
              });
            }
          }
        }
      );
    }
    if (currentAction && rights.includes("Role Rights Edit")) {
      actions.push(
        {
          icon: "check",
          helpText: "Submit Changes",
          order: 9699,
          className: this.getActionClass("submitting"),
          onClick: () => {
            this.setState({
              confirmationModalVisible: true,
            });
          }
        },
        {
          icon: "close",
          helpText: "Cancel Changes",
          order: 9700,
          onClick: () => {
            this.setState({
              currentAction: null,
              selectedTargetRowIndices: [],
            });
          }
        }
      );
    }
    return actions;
  }

  getActionClass(action) {
    const { currentAction, selectedTargetRowIndices } = this.state;
    switch (action) {
      case "adding":
        if (currentAction === action) {
          return "adding active";
        }
        return "";
      case "removing":
        if (currentAction === action) {
          return `removing active`;
        }
        return "";
      case "submitting":
        if (selectedTargetRowIndices.length === 0) {
          return "hide";
        }
        return "";
    }
  }

  getActionHelpText(action) {
    const { columnOrder } = this.state;
    return Capitalize(`${action} ${columnOrder[1] === "permissions" ? "permissions" : "roles"} ${action === "add" ? "to" : "from"} ${columnOrder[1] === "permissions" ? "role" : "permission"}`);
  }

  getGridHeaderText() {
    const { columnOrder, currentAction, selectedTargetRowIndices, viewing } = this.state;
    let output = "";
    if (currentAction) {
      output += Capitalize(`${currentAction} ${viewing} ${columnOrder[1]} to ${columnOrder[0] === "roles" ? "Role" : "Permission"} -`);
    }
    output += selectedTargetRowIndices.length !== 0 ? ` ${selectedTargetRowIndices.length} Selected -` : "";
    return output;
  }

  getTargetGridColumnHeaderText() {
    const { viewing, columnOrder, selectedSourceRowIndex } = this.state;
    return selectedSourceRowIndex ? `text-${viewing}-${columnOrder[1]}` : `text-${columnOrder[1]}`;
  }

  updateSelectedTargetColumns(targetIndex) {
    const { selectedSourceRowIndex, currentAction, selectedTargetRowIndices } = this.state;
    if (!selectedSourceRowIndex || !currentAction) {
      return;
    }
    if (selectedTargetRowIndices.includes(targetIndex)) {
      const selectedTargetRowIndicesIndex = selectedTargetRowIndices.findIndex(selectedTargetRowIndex => selectedTargetRowIndex === targetIndex);
      this.setState({
        selectedTargetRowIndices: [...selectedTargetRowIndices.slice(0, selectedTargetRowIndicesIndex), ...selectedTargetRowIndices.slice(selectedTargetRowIndicesIndex + 1)]
      });
      return;
    }
    this.setState({
      selectedTargetRowIndices: [...selectedTargetRowIndices, targetIndex]
    });
  }

  getSourceId() {
    const { roleRightsViewModel } = this.props;
    const { columnOrder, selectedSourceRowIndex } = this.state;
    return roleRightsViewModel[columnOrder[0]][selectedSourceRowIndex].id;
  }

  getTargetIds() {
    const { selectedTargetRowIndices } = this.state;
    return this.limitTargetResults().filter((targetRow, index) => {
      return selectedTargetRowIndices.includes(index);
    }).map(targetRow => {
      return targetRow.id;
    });
  }

  getTargetNames() {
    const { selectedTargetRowIndices } = this.state;
    return this.limitTargetResults().filter((targetRow, index) => {
      return selectedTargetRowIndices.includes(index);
    }).map(targetRow => {
      return targetRow.name;
    });
  }

  submitChanges() {
    const { addRoleRights, deleteRoleRights } = this.props;
    const { currentAction, selectedTargetRowIndices, columnOrder, viewing } = this.state;
    if (currentAction && selectedTargetRowIndices.length !== 0) {
      let roleRightsPairs;
      let sourceId = this.getSourceId();
      switch (columnOrder[0]) {
        case "roles":
          roleRightsPairs = this.getTargetIds().map(rightId => {
            return { roleId: sourceId, rightId: rightId };
          });
          break;
        case "permissions":
          roleRightsPairs = this.getTargetIds().map(roleId => {
            return { roleId: roleId, rightId: sourceId };
          });
          break;
      }
      let enabled = viewing === "enabled" ? true : false;
      switch (currentAction) {
        case "adding":
          addRoleRights(roleRightsPairs, enabled);
          break;
        case "removing":
          deleteRoleRights(roleRightsPairs, enabled);
          break;
      }
    }
  }

  getConfirmationModalText() {
    const { columnOrder, selectedTargetRowIndices, selectedSourceRowIndex, currentAction, viewing } = this.state;
    const { roleRightsViewModel } = this.props;
    if (selectedSourceRowIndex !== null && selectedTargetRowIndices.length !== 0) {
      let confirmationModalText = `Are you sure you want to ${currentAction === "adding" ? "add" : "remove"} the ${viewing} ${selectedTargetRowIndices.length !== 1 ? `${columnOrder[1]}:<br>` : `${columnOrder[1].slice(0, -1)}: `}`;
      let targetNames = this.getTargetNames();
      for (let i = 0; i < targetNames.length; i++) {
        confirmationModalText += `<strong>${targetNames[i]}</strong>${i !== targetNames.length - 1 ? ",<br>" : ""}`;
      }
      confirmationModalText += `<br>${currentAction === "adding" ? "To" : "From"} the ${columnOrder[0].slice(0, -1)}: <strong>${roleRightsViewModel[columnOrder[0]][selectedSourceRowIndex].name}</strong>`;
      return confirmationModalText;
    }
  }

  render() {
    const { roleRightsViewModel, fetchingRoleRights } = this.props;
    if (!roleRightsViewModel) {
      return <MSpinner />;
    }
    const { columnOrder, selectedSourceRowIndex, selectedTargetRowIndices, currentAction, confirmationModalVisible } = this.state;
    return (
      <div className="role-rights-wrapper full-height padding-page-content">
        <Card className="card-unnested full-height-card">
          <CardContent><Typography variant="h6">Role Permissions</Typography></CardContent>
          <Grid container spacing={2} className="role-rights-grids full-height">
            <Grid size={6} className={`left-grid`}>
              <MGrid
                columnDefinitions={this.getColumnDefinitions(columnOrder[0])}
                headerText={" "}
                data={this.getGridData(columnOrder[0])}
                isFetchingData={fetchingRoleRights}
                onRowClick={this.setSelectedSourceRowIndex}
                selectedRows={selectedSourceRowIndex !== null ? [selectedSourceRowIndex] : []}
                allowRowSelection
                onRowSelectionChanged={selectedSourceRowIndices => this.setState({ selectedSourceRowIndex: selectedSourceRowIndices.find(i => i !== selectedSourceRowIndex) ?? null })}
              />
            </Grid>
            <Grid size={6} className={`right-grid ${this.getTargetGridColumnHeaderText()}`}>
              <MGrid
                key={`${columnOrder[1]}-${currentAction}`}
                columnDefinitions={this.getColumnDefinitions(columnOrder[1])}
                headerText={this.getGridHeaderText()}
                data={this.getGridData(columnOrder[1])}
                isFetchingData={fetchingRoleRights}
                actions={this.getGridActions()}
                onRowClick={this.updateSelectedTargetColumns}
                draggableColumns={false}
                allowRowSelection={!!currentAction}
                selectedRows={currentAction ? selectedTargetRowIndices : []}
                onRowSelectionChanged={selectedTargetRowIndices => this.setState({ selectedTargetRowIndices: selectedTargetRowIndices })}
                downloadFileName={`${roleRightsViewModel?.[columnOrder[0]]?.[selectedSourceRowIndex]?.name} - Role Rights - {date}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Dialog
          open={confirmationModalVisible}
          onClose={() => {
            this.setState({
              confirmationModalVisible: false,
            });
          }}
          id="submit-changes"
          aria-label="Submit changes"
          sx={{ '& .MuiDialog-paper': { width: 750 } }}
        >
          <DialogTitle>Submit Changes</DialogTitle>
          <DialogContent>
            <div>
              <Card className="card-unnested">
                <CardContent>
                  <Typography variant="h6">Submit Changes</Typography>
                </CardContent>
                {/*eslint-disable */}
                <CardContent>
                  <div dangerouslySetInnerHTML={{ __html: this.getConfirmationModalText() }} />
                </CardContent>
                {/*eslint-disable */}
              </Card>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.submitChanges}>submit</Button>
            <Button onClick={() => { this.setState({ confirmationModalVisible: false }); }}>cancel</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

RoleRights.propTypes = {
  //Redux Store State
  roleRightsViewModel: PropTypes.object,
  fetchingRoleRights: PropTypes.bool,
  requestedRoleRightsEdit: PropTypes.bool,
  user: PropTypes.object,

  //Redux Actions
  getRoleRights: PropTypes.func,
  addRoleRights: PropTypes.func,
  deleteRoleRights: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    roleRightsViewModel: state.roleRights.roleRightsViewModel,
    fetchingRoleRights: state.roleRights.fetchingRoleRights,
    requestedRoleRightsEdit: state.roleRights.requestedRoleRightsEdit,
    user: state.user.profile
  };
};

const mapDispatchToProps = dispatch => ({
  getRoleRights() {
    dispatch(getRoleRights());
  },
  addRoleRights(roleRightsPairs, context) {
    dispatch(addRoleRights(roleRightsPairs, context));
  },
  deleteRoleRights(roleRightsPairs, context) {
    dispatch(deleteRoleRights(roleRightsPairs, context));
  },
});

export default
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(RoleRights);
